import React, { useState, useEffect, useRef } from "react";
import TableHeader from "../../common/table/TableHeader";
import TableSkeleton from "../../common/table/TableSkeleton";
import handleError from "../../../constant/handleError";
import checkNetworkStatus from "../../common/networkStatus/CheckNetworkStatus";
import axiosInstance from "../../../utils/AxiosInstance";
import useShowAlert from "../../../utils/AlertUtiles";
import uRLs from "../../../utils/baseUtils";
import { capitalizeFirstLetter } from "../../../utils/helper";
import ReactPaginate from "react-paginate";
import FOView from "./FOView";
import Messages from "../../../constant/Messages";

const ApprovalBadge = ({ status }) => {
  let className = "app-badge  ";
  let displayText = status;
  if (status === "Approved") {
    className += "bg-label-spring";
  } else if (status === "Rejected") {
    className += "bg-danger";
  } else if (status === "Pending") {
    className += "bg-warning ";
  } else if (status === "GANGLEADER") {
    className += "bg-label-navy ";
    displayText = "Captain";
  } else if (status === "FIELDOFFICER") {
    className += "bg-label-wave ";
    displayText = "Field Officer";
  } else {
    className += "bg-light text-dark";
  }

  return <span className={className}>{displayText}</span>;
};

function FOManagement() {
  const [isFoData, setFoData] = useState([]);
  const [roleSelections, setRoleSelections] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const phoneRef = useRef(null);
  const saveButtonRef = useRef(null);
  const nameRef = useRef(null);
  const employeeIdRef = useRef(null);
  const clickLockRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    employeeId: "",
    mobileNumber: "",
    role: "FIELDOFFICER",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [loading, setLoading] = useState({
    list: false,
    update: null,
  });

  const showAlert = useShowAlert();

  const handleRoleChange = (partnerId, role) => {
    setRoleSelections((prev) => ({
      ...prev,
      [partnerId]: role,
    }));
  };

  const fetchPartners = async () => {
    setLoading((prev) => ({ ...prev, list: true }));
    checkNetworkStatus();

    try {
      const response = await axiosInstance.get(
        "partnerauth/admin/list-fieldofficer",
      );
      if (
        response &&
        response?.data?.status &&
        response?.data?.results?.length > 0
      ) {
        setFoData(response.data.results);
      } else {
        setFoData([]);
      }
    } catch (error) {
      setFoData([]);
      handleError(error, showAlert);
      return false;
    } finally {
      setLoading((prev) => ({ ...prev, list: false }));
    }
  };

  const handleViewPartner = (partner) => {
    setSelectedPartner(partner);
    if (!partner.isApproved && !roleSelections[partner._id]) {
      handleRoleChange(partner._id, "");
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPartner(null);
  };
  const handleEnterKey = (e, nextRef = null, isLast = false) => {
    if (e.key !== "Enter") return;

    e.preventDefault();

    const newErrors = {};

    if (e.target.name === "name" && !formData.name.trim()) {
      newErrors.name = Messages.ERROR.NAME_EMPTY;
      setErrors(newErrors);
      nameRef?.current?.focus();
      return;
    }

    if (e.target.name === "employeeId" && !formData.employeeId.trim()) {
      newErrors.employeeId = "Employee ID is required";
      setErrors(newErrors);
      employeeIdRef?.current?.focus();
      return;
    }

    if (e.target.name === "mobileNumber" && !formData.mobileNumber.trim()) {
      newErrors.phone = Messages.ERROR.MOBILE_EMPTY;
      setErrors(newErrors);
      phoneRef?.current?.focus();
      return;
    }

    setErrors({});

    if (isLast) {
      handleAdd(e);
    } else if (nextRef?.current) {
      nextRef.current.focus();
    }
  };
  useEffect(() => {
    fetchPartners();    
  }, []);
  useEffect(() => {
    
    if(isOpen){
     nameRef.current.focus();
    }
  }, [isOpen]);
  const headers = ["S.No", "Partner Name", "Employee ID", "Phone", "Role"];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrors("")
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleAdd = async (e) => {
    e.preventDefault();

    if (clickLockRef.current) return;

    clickLockRef.current = true;

    const errors = {};

    if (!formData.name.trim()) {
      errors.name = Messages.ERROR.NAME_EMPTY;
      nameRef?.current?.focus();
    } else if (!formData.employeeId.trim()) {
      errors.employeeId = "Employee ID is required";
      employeeIdRef?.current?.focus();
    } else if (!formData.mobileNumber.trim()) {
      errors.phone = Messages.ERROR.MOBILE_EMPTY;
      phoneRef?.current?.focus();
    } else if (formData.mobileNumber.trim().length !== 10) {
      errors.phone = "Mobile number must be 10 digits";
      phoneRef?.current?.focus();
    }

    setErrors(errors);

    if (Object.keys(errors).length > 0) {
      clickLockRef.current = false;
      return;
    }

    try {
      const response = await axiosInstance.patch(
        "partnerauth/admin/add-fieldofficer",
        {
          name: formData.name,
          employeeId: formData.employeeId,
          mobileNumber: formData.mobileNumber,
          role: "FIELDOFFICER",
        },
      );

      if (response?.data?.status) {
        showAlert("success", response?.data?.message);

        setFoData((prev) => [response?.data?.results, ...prev]);

        setIsOpen(false);

        setFormData({
          name: "",
          employeeId: "",
          mobileNumber: "",
          role: "FIELDOFFICER",
        });

        setErrors({});
      }
    } catch (error) {
      const code = error.response?.data?.code;
      const message = error.response?.data?.message;
      if (code === "GANGLEADER_CONFLICT") {
        setErrors({ phone: message });
        phoneRef?.current?.focus();
      } else if (code === "EMPLOYEE_ID_CONFLICT") {
        setErrors({ employeeId: message });
        employeeIdRef?.current?.focus();
      } else {
        handleError(error, showAlert);
      }
    } finally {
      clickLockRef.current = false;
    }
  };

  const onClose = () => {
    setIsOpen(false);

    setFormData({
      name: "",
      employeeId: "",
      mobileNumber: "",
      role: "FIELDOFFICER",
    });
  };
  return (
    <>
      <div className=" pe-3">
        <div className="d-flex align-items-center justify-content-between mb-1">
          <div className="breadcrumb-title pe-3 mb-3">
            Field Leader Management
          </div>
          <button
            className="btn btn-primary text-right"
            onClick={() => setIsOpen(true)}
          >
            <i className="bx bx-plus" />
            Add
          </button>
        </div>
      </div>

      <div className="col-col-12 ">
        <div className="card mb-0">
          <div className="card-body p-0">
            <table className="table align-middle table-hover mb-0 cursor-pointer ">
              <TableHeader headers={headers} />

              <tbody>
                {loading.list ? (
                  <TableSkeleton rows={5} columns={[300, 200, 100, 100]} />
                ) : Array.isArray(isFoData) && isFoData.length > 0 ? (
                  isFoData.map((partner, index) => (
                    <tr
                      key={partner._id || index}
                      onClick={() => handleViewPartner(partner)}
                    >
                      <td className="text-center">{index + 1}</td>

                      <td className="text-center">
                        {capitalizeFirstLetter(partner.name) || "-"}
                      </td>
                      <td className="text-center">
                        {partner?.employeeId || "-"}
                      </td>
                      <td className="text-center">
                        <p className="mb-0 text-center fw-bold">
                          {partner?.mobileNumber}
                        </p>
                      </td>

                      <td className="text-center">
                        {partner?.role ? (
                          <ApprovalBadge status={partner?.role} />
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center p-2 text-muted">
                      <span className="text-muted">
                        No partners found in this list.
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {isModalOpen && (
            <FOView
              onClose={handleCloseModal}
              partner={selectedPartner}
              roleSelections={roleSelections}
              handleRoleChange={handleRoleChange}
              // onApprove={approvePartner}
              loadingUpdate={loading.update}
            />
          )}
        </div>
      </div>
      {isOpen && (
        <>
          <div className="modal-backdrop show"></div>

          <div
            className="modal show d-block"
            tabIndex="-1"
            aria-modal="true"
            role="dialog"
          >
            <div className="modal-dialog modal-dialog-centered modal-md">
              <div className="modal-content ">
                <div className="modal-header border-bottom-0 py-3">
                  <h6 className="modal-title fw-semibold">Add Field Officer</h6>
                  <button
                    type="button"
                    className="btn-close border"
                    onClick={onClose}
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  ></button>
                </div>

                {/* Body */}
                <div className="modal-body p-4">
                  <div className="row">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Full Name <span className="text-danger">*</span>
                      </label>

                      <div className="input-group">
                        {/* <span className="input-group-text bg-light">
                  <i className="bx bx-user"></i>
                </span> */}
                        <input
                          type="text"
                          ref={nameRef}
                          className="form-control"
                          name="name"
                          placeholder=""
                          value={formData.name}
                          onKeyDown={(e) => handleEnterKey(e, employeeIdRef)}
                          onChange={handleChange}
                        />
                        {errors.name && (
                          <div className="error-message invalid-feedback">
                            {errors.name}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Employee ID <span className="text-danger">*</span>
                      </label>

                      <div className="input-group">
                        <input
                          type="text"
                          ref={employeeIdRef}
                          className="form-control"
                          name="employeeId"
                          placeholder=""
                          value={formData.employeeId}
                          onKeyDown={(e) => handleEnterKey(e, phoneRef)}
                          onChange={handleChange}
                        />
                        {errors.employeeId && (
                          <div className="error-message invalid-feedback">
                            {errors.employeeId}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Mobile Number <span className="text-danger">*</span>
                      </label>

                      <div className="input-group">
                        {/* <span className="input-group-text bg-light">
                  <i className="bx bx-phone"></i>
                </span> */}

                        <input
                          type="text"
                          ref={phoneRef}
                          className="form-control"
                          name="mobileNumber"
                          placeholder=""
                          value={formData.mobileNumber}
                          maxLength={10}
                          onChange={handleChange}
                          onKeyDown={(e) =>
                            handleEnterKey(e, saveButtonRef, true)
                          }
                        />
                        {errors.phone && (
                          <div className="error-message invalid-feedback">
                            {errors.phone}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Role */}
                    <div className="mb-2">
                      <label className="form-label fw-semibold">Role</label>

                      <div className="row g-3">
                        <div className="col-12">
                          <div
                            className={`border rounded-4 p-2 cursor-pointer ${
                              formData.role === "FIELDOFFICER"
                                ? "border-primary bg-primary-subtle"
                                : ""
                            }`}
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                role: "FIELDOFFICER",
                              }))
                            }
                          >
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div
                                  className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                  style={{
                                    width: "48px",
                                    height: "48px",
                                    background: "#eef3ff",
                                    color: "#5f82c5",
                                  }}
                                >
                                  <i className="bx bx-id-card fs-4"></i>
                                </div>

                                <div>
                                  <h6 className="mb-1 fw-bold">
                                    Field Officer
                                  </h6>

                                  {/* <small className="text-muted">
                                    Handles field visits and farmer support
                                  </small> */}
                                </div>
                              </div>

                              <input
                                type="radio"
                                className="me-2"
                                checked={formData.role === "FIELDOFFICER"}
                                readOnly
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Footer */}
                <div className="modal-footer d-flext justify-content-between">
                  <button
                    type="button"
                    className="btn btn-outline-secondary text-left"
                    onClick={onClose}
                  >
                    <i className="bx bx-x"></i> Close
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary px-4"
                    onClick={handleAdd}
                  >
                    <i className="bx bx-plus me-1"></i>
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default FOManagement;
